/**
 * DownloadPage — public landing page at /download (and /download?ref=<invite-slug>).
 *
 * If a personalised invite is in session storage we surface the recipient's name
 * and the demo credentials. Otherwise we render the generic download page.
 *
 * Auto-fires invite events when the recipient taps an install button — gives
 * the admin a real "click → install button tapped" funnel.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Apple, Smartphone, QrCode, Globe2, ShieldCheck, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function DownloadPage() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!ref) return;
    const cached = sessionStorage.getItem(`diplomexa.invite.${ref}`);
    if (cached) {
      try {
        setInvite(JSON.parse(cached));
        return;
      } catch { /* corrupt cache — fall through to network fetch */ }
    }
    api.get(`/invites/${ref}/track`).then((r) => {
      sessionStorage.setItem(`diplomexa.invite.${ref}`, JSON.stringify(r.data));
      setInvite(r.data);
    }).catch(() => {});
  }, [ref]);

  // Per-installer-button event hook
  const fireEvent = async (event) => {
    if (!ref) return;
    api.post(`/invites/${ref}/event`, { event }).catch(() => {});
  };

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      toast.success(`Copied ${key}`);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error("Could not copy.");
    }
  };

  const iosUrl = invite?.ios_url || "";
  const androidUrl = invite?.android_url || "";
  const headline = useMemo(() => {
    if (invite?.name) return `${invite.name.split(" ")[0]}, your Diplomexa preview is ready.`;
    return "Diplomexa is now on iPhone + Android.";
  }, [invite]);

  return (
    <div className="min-h-screen text-white" style={{ background: "radial-gradient(circle at 50% -10%, #14264F 0%, #0A192F 55%, #050C1B 100%)" }} data-testid="download-page">
      {/* Header */}
      <header className="px-6 pt-8 max-w-6xl mx-auto flex items-center justify-between">
        <BrandLogo light />
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40">
          PREVIEW BUILD · INVITE-ONLY
        </span>
      </header>

      {/* Hero */}
      <main className="max-w-5xl mx-auto px-6 pt-12 pb-24">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold">
          DIPLOMEXA · BY FACTSKOOT
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] max-w-3xl"
        >
          {headline}
        </motion.h1>
        <p className="mt-5 text-slate-300 max-w-2xl text-lg leading-relaxed">
          The Global Conference & Leadership OS, in your pocket.
          {invite?.organization
            ? ` Custom-shared for ${invite.organization} — install on your phone in 60 seconds.`
            : " Install on your phone in 60 seconds."}
        </p>

        {/* Install grid */}
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          <InstallCard
            icon={<Apple className="h-6 w-6" />}
            title="iPhone & iPad"
            sub="TestFlight · iOS 16+"
            cta="Open TestFlight"
            href={iosUrl || "#"}
            disabled={!iosUrl}
            onClick={() => fireEvent("ios_button_click")}
            secondary="Requires the free TestFlight app from the App Store."
            testId="install-ios"
          />
          <InstallCard
            icon={<Smartphone className="h-6 w-6" />}
            title="Android"
            sub=".apk direct install · Android 9+"
            cta="Download APK"
            href={androidUrl || "#"}
            disabled={!androidUrl}
            onClick={() => fireEvent("android_button_click")}
            secondary='Tap "Allow installs from this source" the first time.'
            testId="install-android"
          />
        </div>

        {/* Also available */}
        <div className="mt-10 flex flex-wrap items-center gap-4 text-sm text-slate-300">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Also available</span>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            data-testid="install-web"
            onClick={() => fireEvent("web_link_click")}
          >
            <Globe2 className="h-3.5 w-3.5 text-[#D4AF37]" />
            Web app
          </a>
          <a
            href="/scan"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            data-testid="install-scan"
          >
            <QrCode className="h-3.5 w-3.5 text-[#D4AF37]" />
            Diplomexa Verify (PWA)
          </a>
        </div>

        {/* Demo credentials box (only when invite present) */}
        {invite && (invite.demo_email || invite.demo_password) && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-12 rounded-2xl bg-white/5 border border-[#D4AF37]/20 p-6"
            data-testid="invite-demo-creds"
          >
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
              YOUR DEMO LOGIN
            </p>
            <h3 className="mt-2 text-xl font-bold text-white">Sign in once · explore all 12 dashboards</h3>
            <p className="mt-1 text-sm text-slate-400">
              These credentials work on iOS, Android and the web app. Tap to copy.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <CredentialRow label="Email" value={invite.demo_email} copied={copied === "Email"} onCopy={() => copy("Email", invite.demo_email)} testId="copy-demo-email" />
              <CredentialRow label="Password" value={invite.demo_password} copied={copied === "Password"} onCopy={() => copy("Password", invite.demo_password)} testId="copy-demo-password" />
            </div>
          </motion.div>
        )}

        {/* Why bother */}
        <div className="mt-16 grid sm:grid-cols-3 gap-5">
          <Highlight
            icon={<Sparkles className="h-5 w-5 text-[#D4AF37]" />}
            title="Diplomexa AI"
            body="Country-perfect briefs and bloc strategy in seconds — for delegates and chairs."
          />
          <Highlight
            icon={<ShieldCheck className="h-5 w-5 text-[#D4AF37]" />}
            title="Verified certificates"
            body="QR-signed certificates that anyone can verify with their phone camera in 1 second."
          />
          <Highlight
            icon={<QrCode className="h-5 w-5 text-[#D4AF37]" />}
            title="Live everything"
            body="Real-time motions, polls, crisis push, and a 60-second Conference Health Score."
          />
        </div>

        {/* Footer */}
        <footer className="mt-20 pt-8 border-t border-white/5 text-xs text-slate-500">
          Diplomexa by Factskoot · Built by Factskoot Eduwellness Pvt Ltd ·
          From Conferences to Global Citizens · <a href="/privacy" className="hover:text-white">Privacy</a> · <a href="/terms" className="hover:text-white">Terms</a>
        </footer>
      </main>
    </div>
  );
}

function InstallCard({ icon, title, sub, cta, href, disabled, onClick, secondary, testId }) {
  // Inline conditional: avoid defining a nested component during render
  // (React would unmount + remount the whole subtree on every parent re-render).
  const className = `group rounded-2xl bg-white/[0.04] border border-white/10 p-7 transition-all ${
    disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/[0.07] hover:-translate-y-0.5 hover:border-[#D4AF37]/40"
  }`;
  const body = (
    <>
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">{sub}</p>
          <h3 className="text-lg font-bold mt-0.5 text-white">{title}</h3>
          <p className="mt-3 text-xs text-slate-400 leading-relaxed">{secondary}</p>
        </div>
      </div>
      <Button
        className="mt-6 w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
        disabled={disabled}
      >
        {disabled ? "Coming soon" : cta}
      </Button>
    </>
  );
  if (disabled) {
    return (
      <div className={className} data-testid={testId}>
        {body}
      </div>
    );
  }
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      data-testid={testId}
    >
      {body}
    </a>
  );
}

function CredentialRow({ label, value, onCopy, copied, testId }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      className="text-left rounded-lg bg-[#0A192F] border border-white/10 p-4 hover:border-[#D4AF37]/40 transition-colors"
    >
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-3">
        <p className="text-white font-mono text-sm truncate" data-testid={`creds-${label.toLowerCase()}`}>{value || "—"}</p>
        {copied ? <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" /> : <Copy className="h-4 w-4 text-slate-500 flex-shrink-0" />}
      </div>
    </button>
  );
}

function Highlight({ icon, title, body }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-5">
      <div className="h-9 w-9 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center">
        {icon}
      </div>
      <h4 className="mt-3 text-sm font-bold text-white">{title}</h4>
      <p className="mt-1 text-xs text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}
